"use client";

import { AUTH_COOKIE_KEY, AUTH_STORAGE_KEY, AUTH_TOKEN_STORAGE_KEY } from "@/shared/config/constants";
import { deleteClientCookie, setClientCookie } from "@/shared/lib/cookie.client";
import { getLocalStorageValue, removeLocalStorageValue, setLocalStorageValue } from "@/shared/lib/local-storage.client";

export type OrgRole = "owner" | "admin" | "member";

export type AuthUser = {
  id: number;
  first_name: string;
  last_name: string;
  phone: string;
  organization_id?: string | null;
  org_role?: OrgRole | null;
  is_admin?: boolean;
};

export type AuthSession = {
  user: AuthUser;
  // Bearer-token fallback for clients where the HttpOnly cookie can't ride
  // along (cross-site third-party cookie blocking). Optional because legacy
  // cached sessions (cookie-only) won't have it.
  token?: string;
};

let sessionRevision = 0;
let isSessionExpired = false;
const expirationListeners = new Set<() => void>();

export function getAuthSessionVersion(): string {
  // Storage values also detect account changes made by another browser tab.
  return JSON.stringify([sessionRevision, getLocalStorageValue(AUTH_STORAGE_KEY), getStoredAuthToken()]);
}

export function isAuthSessionCurrent(version: string): boolean {
  return version === getAuthSessionVersion();
}

export function subscribeToAuthSessionExpiration(listener: () => void) {
  expirationListeners.add(listener);
  return () => {
    expirationListeners.delete(listener);
  };
}

export function expireAuthSession(version: string) {
  if (isSessionExpired || !isAuthSessionCurrent(version)) return;
  clearStoredAuthSession();
  expirationListeners.forEach((listener) => listener());
}

export function getStoredAuthToken(): string | null {
  return getLocalStorageValue(AUTH_TOKEN_STORAGE_KEY);
}

export function setStoredAuthToken(token: string) {
  sessionRevision += 1;
  isSessionExpired = false;
  setLocalStorageValue(AUTH_TOKEN_STORAGE_KEY, token);
}

export function clearStoredAuthToken() {
  sessionRevision += 1;
  removeLocalStorageValue(AUTH_TOKEN_STORAGE_KEY);
}

export function getStoredAuthSession(): AuthSession | null {
  const raw = getLocalStorageValue(AUTH_STORAGE_KEY);
  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as Partial<AuthSession> | null;
    if (!parsed?.user?.id) {
      return null;
    }

    return parsed as AuthSession;
  } catch {
    return null;
  }
}

// Keep only non-sensitive client-side auth state here. The real session token is
// held by the backend in an HttpOnly cookie when the browser allows it; the
// bearer token is also stored separately (see setStoredAuthToken) as a fallback
// for clients that block third-party cookies.
export function setStoredAuthSession(session: AuthSession) {
  sessionRevision += 1;
  isSessionExpired = false;
  const { token: _ignored, ...userOnly } = session;
  setLocalStorageValue(AUTH_STORAGE_KEY, JSON.stringify(userOnly));
  setClientCookie(AUTH_COOKIE_KEY, "1");
  if (session.token) {
    setStoredAuthToken(session.token);
  }
}

export function clearStoredAuthSession() {
  isSessionExpired = true;
  removeLocalStorageValue(AUTH_STORAGE_KEY);
  deleteClientCookie(AUTH_COOKIE_KEY);
  clearStoredAuthToken();
}

export function formatAuthUserName(user: Pick<AuthUser, "first_name" | "last_name" | "phone"> | null | undefined) {
  if (!user) {
    return "Unknown user";
  }

  const fullName = [user.first_name, user.last_name].filter(Boolean).join(" ").trim();
  return fullName || user.phone || "Unknown user";
}
